import React, { useState } from 'react';
import { NotepadText, Send, Edit, Trash2, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const CrewLogs = () => {
  const [logEntries, setLogEntries] = useState([
    {
      id: 1,
      time_utc: '2025-11-05T02:14:31Z',
      scope: 'Science',
      author: 'Dr. L. Kaur',
      role: 'Chief Scientist',
      text: 'Strong thermocline at 140–160 m. Shift CTD plan accordingly.',
    },
    {
      id: 2,
      time_utc: '2025-11-05T01:47:02Z',
      scope: 'Bridge',
      author: 'M. Ortega',
      role: '1st Officer',
      text: 'Course adjusted 10° to avoid squall line; speed maintained 9.8 kn.',
    },
    {
      id: 3,
      time_utc: '2025-11-05T00:22:15Z',
      scope: 'Engineering',
      author: 'K. Matsuda',
      role: 'Chief Engineer',
      text: 'Port thruster maintenance completed. All systems nominal.',
    },
  ]);

  const [newLog, setNewLog] = useState('');
  const [selectedScope, setSelectedScope] = useState('Bridge');
  const [editingLog, setEditingLog] = useState(null);
  const [editText, setEditText] = useState('');
  const [editScope, setEditScope] = useState('');

  const handleSubmit = () => {
    if (!newLog.trim()) return;

    const entry = {
      id: Date.now(),
      time_utc: new Date().toISOString(),
      scope: selectedScope,
      author: 'Current User',
      role: 'Officer',
      text: newLog,
    };

    setLogEntries([entry, ...logEntries]);
    setNewLog('');
  };

  const handleEdit = (log) => {
    setEditingLog(log.id);
    setEditText(log.text);
    setEditScope(log.scope);
  };

  const handleSaveEdit = (logId) => {
    setLogEntries(logEntries.map(log => 
      log.id === logId 
        ? { ...log, text: editText, scope: editScope }
        : log
    ));
    setEditingLog(null);
    setEditText('');
    setEditScope('');
  };

  const handleCancelEdit = () => {
    setEditingLog(null);
    setEditText('');
    setEditScope('');
  };

  const handleDelete = (logId) => {
    if (window.confirm('Are you sure you want to delete this log entry?')) {
      setLogEntries(logEntries.filter(log => log.id !== logId));
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toISOString().substr(11, 8) + ' UTC';
  };

  const getScopeColor = (scope) => {
    const colors = {
      Science: 'border-chart-1/30 text-chart-1 bg-chart-1/10',
      Bridge: 'border-chart-2/30 text-chart-2 bg-chart-2/10',
      Engineering: 'border-chart-4/30 text-chart-4 bg-chart-4/10',
    };
    return colors[scope] || 'border-muted-foreground/30 text-muted-foreground bg-muted/10';
  };

  return (
    <Card className="col-span-12 xl:col-span-7 animate-fade-in" data-testid="crew-logs">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <NotepadText className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Crew Logs</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Log composer */}
        <div className="mb-6 p-4 rounded-md bg-secondary/40 border border-border/40" data-testid="log-composer">
          <Textarea
            placeholder="Add a log entry…"
            value={newLog}
            onChange={(e) => setNewLog(e.target.value)}
            className="mb-3 min-h-[80px] resize-none"
            data-testid="log-input"
          />
          <div className="flex items-center gap-2">
            <select
              value={selectedScope}
              onChange={(e) => setSelectedScope(e.target.value)}
              className="h-9 rounded-md bg-secondary text-foreground border border-border px-3 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
              data-testid="log-scope-select"
            >
              <option value="Bridge">Bridge</option>
              <option value="Science">Science</option>
              <option value="Engineering">Engineering</option>
            </select>
            <Button 
              onClick={handleSubmit} 
              size="sm" 
              className="ml-auto"
              data-testid="log-submit-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Post
            </Button>
          </div>
        </div>

        {/* Log entries */}
        <div className="space-y-3" data-testid="log-entries">
          {logEntries.map((entry) => {
            const isEditing = editingLog === entry.id;
            
            return (
              <div 
                key={entry.id} 
                className="p-4 rounded-md bg-secondary/20 border border-border/30 hover:border-border/50 transition-colors"
                data-testid={`log-entry-${entry.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium" data-testid={`log-author-${entry.id}`}>{entry.author}</span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">{entry.role}</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono" data-testid={`log-time-${entry.id}`}>
                      {formatTime(entry.time_utc)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <select
                        value={editScope}
                        onChange={(e) => setEditScope(e.target.value)}
                        className="h-7 rounded-md bg-secondary text-foreground border border-border px-2 text-xs focus:ring-2 focus:ring-ring focus:outline-none"
                        data-testid={`log-edit-scope-${entry.id}`}
                      >
                        <option value="Bridge">Bridge</option>
                        <option value="Science">Science</option>
                        <option value="Engineering">Engineering</option>
                      </select>
                    ) : (
                      <Badge 
                        variant="outline" 
                        className={getScopeColor(entry.scope)}
                        data-testid={`log-scope-${entry.id}`}
                      >
                        {entry.scope}
                      </Badge>
                    )}
                  </div>
                </div>
                
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="min-h-[60px] resize-none text-sm"
                      data-testid={`log-edit-input-${entry.id}`}
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleSaveEdit(entry.id)}
                        size="sm"
                        variant="default"
                        className="h-7 text-xs"
                        data-testid={`log-save-${entry.id}`}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Save
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        data-testid={`log-cancel-${entry.id}`}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-foreground flex-1" data-testid={`log-text-${entry.id}`}>{entry.text}</p>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        onClick={() => handleEdit(entry)}
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        data-testid={`log-edit-btn-${entry.id}`}
                      >
                        <Edit className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(entry.id)}
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        data-testid={`log-delete-btn-${entry.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CrewLogs;
